import { unwrapElement } from '../unwrapElement.js';
/**
 * Vue directive that scrolls an element into view when mounted or when the trigger value changes.
 * Provides a declarative way to handle scrolling to specific elements.
 *
 * @example
 * ```vue
 * <template>
 *   <!-- Auto-scroll when mounted -->
 *   <div v-scroll-into-view></div>
 *
 *   <!-- Conditional scrolling -->
 *   <div v-scroll-into-view="{ trigger: shouldScroll }"></div>
 *
 *   <!-- With scroll options -->
 *   <div v-scroll-into-view="{
 *     trigger: shouldScroll,
 *     options: { behavior: 'smooth', block: 'center' }
 *   }"></div>
 * </template>
 *
 * <script setup lang="ts">
 * import { ref } from 'vue';
 *
 * const shouldScroll = ref(false);
 *
 * function scrollToElement() {
 *   shouldScroll.value = true;
 * }
 * </script>
 * ```
 */
export const vScrollIntoView = {
    mounted(instance, binding) {
        const el = unwrapElement(instance);
        const enabled = isEnabled(binding.value?.trigger);
        if (el && enabled && el instanceof HTMLElement) {
            setTimeout(() => el.scrollIntoView(binding.value?.options));
        }
    },
    updated(instance, binding) {
        const el = unwrapElement(instance);
        const oldTrigger = binding.oldValue?.trigger;
        const newTrigger = binding.value?.trigger;
        if (oldTrigger === newTrigger) {
            return;
        }
        const enabled = isEnabled(newTrigger);
        if (el && enabled && el instanceof HTMLElement) {
            setTimeout(() => el.scrollIntoView(binding.value?.options));
        }
    },
};
function isEnabled(trigger) {
    return trigger === undefined || !!trigger;
}
//# sourceMappingURL=vScrollIntoView.js.map